import React from 'react';
import { Box, ButtonBase, Container, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import colors from '../../assets/theme/colors';

const getFirstName = (user) => String(user?.displayName || '').trim().split(' ')[0] || '';

export const getGreeting = (date = new Date()) => {
  const hour = date.getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
};

export const HomeHeader = ({ activeChild, user }) => {
  const firstName = getFirstName(user);
  return (
    <Stack spacing={2.3}>
      <Box>
        <Typography component="h1" sx={{ fontSize: { xs: '1.65rem', sm: '2rem' }, fontWeight: 950, color: colors.semantic.ink, lineHeight: 1.1 }}>
          {getGreeting()}{firstName ? `, ${firstName}` : ''}
        </Typography>
        <Typography sx={{ mt: 0.5, fontSize: { xs: '0.98rem', sm: '1.08rem' }, color: colors.semantic.ink2 }}>Here&apos;s what&apos;s happening with {activeChild.name || 'your profile'}.</Typography>
      </Box>
    </Stack>
  );
};

export const CaptureButton = ({ onClick }) => (
  <ButtonBase onClick={onClick} data-cy="home-capture-cta" data-testid="home-capture-cta" sx={{ width: '100%', justifyContent: 'center', gap: 1, px: 2, py: { xs: 1.45, sm: 1.65 }, borderRadius: { xs: '18px', sm: '22px' }, bgcolor: colors.semantic.sageAction, color: colors.semantic.sageActionInk, boxShadow: `0 10px 24px ${alpha(colors.semantic.sageAction, 0.2)}`, '& .MuiTypography-root': { color: 'inherit' }, '&:hover': { bgcolor: colors.semantic.sageActionHover } }}>
    <Box sx={{ width: 28, height: 28, borderRadius: '50%', display: 'grid', placeItems: 'center', bgcolor: '#FFFFFF', color: colors.semantic.sageActionInk }}><AddRoundedIcon sx={{ fontSize: 21 }} /></Box>
    <Typography sx={{ fontSize: { xs: '1rem', sm: '1.1rem' }, fontWeight: 850 }}>Capture something</Typography>
  </ButtonBase>
);

const navItems = [
  { label: 'Home', Icon: HomeRoundedIcon, action: 'home' },
  { label: 'Timeline', Icon: HistoryRoundedIcon, action: 'timeline' },
  { label: 'Care Profile', Icon: PersonRoundedIcon, action: 'profile' },
  { label: 'Insights', Icon: AutoAwesomeRoundedIcon, action: 'insights' },
];

export const HomeBottomNav = ({ actions }) => (
  <Box component="nav" aria-label="Mobile care sections" sx={{ display: { xs: 'block', md: 'none' }, position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 10, height: 72, borderTop: `1px solid ${colors.semantic.line}`, bgcolor: alpha('#FFFFFF', 0.98), backdropFilter: 'blur(16px)' }}>
    <Container maxWidth="sm" sx={{ height: '100%', display: 'flex' }}>
      {navItems.map(({ label, Icon, action }) => {
        const active = action === 'home';
        return (
          <ButtonBase key={action} onClick={actions[action]} sx={{ flex: 1, height: '100%', display: 'flex', flexDirection: 'column', gap: 0.4, justifyContent: 'center', color: active ? colors.semantic.sageAction : colors.semantic.ink2 }}>
            <Icon sx={{ fontSize: 23 }} />
            <Typography sx={{ fontSize: '0.67rem', fontWeight: active ? 900 : 700 }}>{label}</Typography>
          </ButtonBase>
        );
      })}
    </Container>
  </Box>
);

export const HomeDesktopNav = ({ actions }) => (
  <Stack component="nav" aria-label="Care sections" direction="row" spacing={0.6} sx={{ display: { xs: 'none', md: 'flex' }, mt: 2 }}>
    {navItems.map(({ label, action }) => (
      <ButtonBase key={action} onClick={actions[action]} sx={{ px: 1.5, py: 0.75, borderRadius: '999px', bgcolor: action === 'home' ? colors.semantic.sageMist : 'transparent', color: action === 'home' ? colors.semantic.sageAction : colors.semantic.ink2 }}>
        <Typography sx={{ fontSize: '0.86rem', fontWeight: action === 'home' ? 850 : 700 }}>{label}</Typography>
      </ButtonBase>
    ))}
  </Stack>
);
